import React, { useMemo } from "react";
import {
  Box,
  Toolbar,
  Typography,
  Button,
  Chip,
  useMediaQuery,
} from "@mui/material";
import {
  Assignment,
  Explore,
  Groups,
  HelpOutline,
  MailOutline,
  Science,
} from "@mui/icons-material/";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useDualMsal } from "../contexts/MsalContext";
import { useTour } from "../contexts/TourContext";
import sharedConfig from "../../shared-config.json";

// Everything public lives on the landing page, so the nav is a set of
// in-page anchors. `secondary` items are dropped on narrower screens to
// leave room for the ones that matter most; phones keep a shorter list
// still (see `pages` below).
const sectionPages = [
  {
    label: "The science",
    target: "science",
    icon: <Science />,
    secondary: true,
  },
  { label: "How it works", target: "how-it-works", icon: <Assignment /> },
  { label: "FAQs", target: "faq", icon: <HelpOutline /> },
  { label: "Who we are", target: "team", icon: <Groups />, secondary: true },
  { label: "Contact", target: "contact", icon: <MailOutline /> },
];

const findSection = (target) =>
  sectionPages.find((page) => page.target === target);

// The participant page keeps a bar of its own: nothing but the tour. Every
// other link leads away from the work in front of them, and what those links
// explained is on the page itself.
const tourPage = { label: "Tour", icon: <Explore />, action: "tour" };

/**
 * Which links the bar shows, as a pure function of who is looking and how
 * wide their screen is.
 *
 * Lifted out of the component so every state can be asserted without a DOM.
 * Both bugs this bar has had came from a combination nobody had enumerated —
 * first that the list is not always five items, then which two survive on a
 * phone — and that is precisely what a table of cases catches.
 */
export function navPages({
  pathname,
  isAuthenticated,
  currentUser,
  isPhone,
  isNarrowPhone,
}) {
  if (pathname === "/participant") return [tourPage];

  const nextPages = [...sectionPages];
  let accountPage = null;

  if (isAuthenticated && currentUser) {
    // Check if this is a B2C or Entra account
    const isB2CAccount = currentUser.accountType === "B2C";
    const hasRoles = currentUser.roles.length > 0;

    // Add My Relmed ONLY for participants (B2C accounts or Entra accounts with participant role)
    // AND only if they are NOT an admin or PI
    const isAdminOrPI =
      currentUser.roles.includes(sharedConfig.roles.admin.name) ||
      currentUser.roles.includes(sharedConfig.roles.pi.name);
    if (
      (isB2CAccount ||
        (hasRoles &&
          currentUser.roles.includes(sharedConfig.roles.participant.name))) &&
      !isAdminOrPI
    ) {
      accountPage = { label: "My Tasks", route: "/participant" };
    }

    // Add Administer for admin/PI roles (Entra accounts only)
    if (hasRoles && isAdminOrPI) {
      accountPage = { label: "Administer", route: "/admin" };
    }

    if (accountPage) nextPages.push(accountPage);
  }

  if (!isPhone) return nextPages;

  // The whole set overflows a phone bar and leaves it scrolling sideways, so
  // keep only what someone in this state came for.
  if (accountPage) {
    if (accountPage.route === "/admin") return [accountPage];
    return isNarrowPhone ? [accountPage] : [findSection("faq"), accountPage];
  }

  // FAQs and Contact rather than How it works and FAQs: this page is what
  // someone reaches by scanning a code at a pharmacy counter, before they
  // have decided whether to take part, and "how do I reach a human about
  // this?" is a question people have at exactly that moment. How it works
  // largely repeats what the page tells them as they scroll; Contact does
  // not appear anywhere else in the bar.
  return [findSection("faq"), findSection("contact")];
}

function RelmedAppBar({ onLoginClick }) {
  const { isAuthenticated, currentUser, logout } = useDualMsal();
  const { openTour } = useTour();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  // Two links still fit side by side on a normal phone, but not on the
  // narrowest ones, where the account link is the only one worth keeping.
  const isNarrowPhone = useMediaQuery("(max-width:359.95px)");

  // Derived on render rather than in an effect: computing it after paint
  // showed the full public menu for a frame on the participant page, so the
  // items we mean to hide flashed up before disappearing.
  const pages = useMemo(
    () =>
      navPages({
        pathname: location.pathname,
        isAuthenticated,
        currentUser,
        isPhone,
        isNarrowPhone,
      }),
    [isAuthenticated, currentUser, location.pathname, isPhone, isNarrowPhone]
  );

  // One link fits on the same row as everything else, even on a phone.
  const singleLink = pages.length === 1;

  const loginChipSx = {
    bgcolor: "#FFF",
    color: theme.palette.primary.main,
    fontWeight: 600,
    px: 0.5,
    "&:hover": { bgcolor: "rgba(255, 255, 255, 0.86)" },
  };

  const handleMenuItemClick = (page) => {
    if (page.action === "tour") {
      openTour();
    } else if (page.route) {
      navigate(page.route);
    } else if (location.pathname === "/") {
      // Already on the landing page: scroll straight there, so clicking the
      // same item twice still works (the hash alone would not change).
      document
        .getElementById(page.target)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate(`/#${page.target}`);
    }
  };

  return (
    <Box
      sx={{
        background: theme.palette.primary.main,
        color: "#FFF",
      }}
    >
      {/* On phones there is no menu: the log in button and the section
          links all sit in the bar, with the links on their own scrollable
          row. A single link needs no row of its own, so on the participant
          page it sits inline between the log out chip and the logo. */}
      <Toolbar
        sx={{
          maxWidth: "xl",
          margin: "auto",
          flexWrap: { xs: singleLink ? "nowrap" : "wrap", md: "nowrap" },
          rowGap: 0.5,
          py: { xs: 1, md: 0 },
        }}
      >
        <Box sx={{ flex: { xs: "0 0 auto", md: "1 1 0" }, display: "flex" }}>
          {isAuthenticated && (
            <Chip
              label="Log out"
              sx={loginChipSx}
              onClick={() => {
                logout();
              }}
            />
          )}
          {!isAuthenticated && (
            <Chip label="Log in" sx={loginChipSx} onClick={onLoginClick} />
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            // The phone row holds two links at most now, so it fits without
            // scrolling and can sit centred like the wide one.
            justifyContent: "center",
            flexGrow: 1,
            order: singleLink ? 0 : { xs: 3, md: 0 },
            flexBasis: singleLink ? "auto" : { xs: "100%", md: "auto" },
            overflowX: "auto",
            // Hide the scrollbar; the row is short and swipeable.
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {pages.map((page) => (
            <Button
              key={page.label}
              onClick={() => handleMenuItemClick(page)}
              sx={{
                color: "#FFF",
                mx: { xs: 0.25, md: 1 },
                flexShrink: 0,
                // Secondary sections give up their space first on mid-width
                // screens; phones drop down to the short list above.
                display: page.secondary
                  ? { xs: "flex", md: "none", lg: "flex" }
                  : "flex",
                textTransform: "none",
                whiteSpace: "nowrap",
              }}
            >
              {page.icon}
              <Typography
                sx={{
                  // The authenticated-only entries have no icon, so the
                  // gap would otherwise be a dangling indent.
                  ml: page.icon ? 1 : 0,
                  color: "#FFF",
                  fontWeight: 500,
                  fontSize: { xs: "0.95rem", md: "1rem" },
                }}
                textAlign="center"
              >
                {page.label}
              </Typography>
            </Button>
          ))}
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flex: { xs: "1 1 auto", md: "1 1 0" },
            alignItems: "center",
          }}
        >
          <Box
            component="img"
            src="/img/UCL-white.svg"
            alt="University College London"
            sx={{ height: { xs: 26, md: 32 }, display: "block" }}
          />
        </Box>
      </Toolbar>
    </Box>
  );
}
export default RelmedAppBar;
